import type { Ingredient } from '../types';

export function formatMinutes(m: number): string {
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
}

// Keyword lists used to classify ingredients into the five components.
// Lowercased substring match against the ingredient name.
const PROTEIN_KEYWORDS = [
  'chicken', 'beef', 'pork', 'lamb', 'turkey', 'duck', 'bacon', 'sausage',
  'ham', 'prosciutto', 'salami', 'chorizo',
  'fish', 'salmon', 'tuna', 'cod', 'tilapia', 'shrimp', 'prawn', 'scallop',
  'crab', 'lobster', 'clam', 'mussel', 'oyster', 'squid', 'anchovy',
  'tofu', 'tempeh', 'seitan', 'edamame', 'lentil', 'chickpea', 'bean',
  'egg', 'paneer',
];
const GRAIN_KEYWORDS = [
  'rice', 'noodle', 'pasta', 'spaghetti', 'penne', 'macaroni', 'udon',
  'soba', 'ramen', 'vermicelli', 'orzo', 'couscous', 'quinoa', 'barley',
  'oat', 'farro', 'bulgur', 'polenta', 'bread', 'tortilla', 'pita',
  'naan', 'bun', 'roll', 'cracker', 'flour',
];
const VEGGIE_KEYWORDS = [
  'onion', 'garlic', 'shallot', 'leek', 'scallion', 'chive',
  'carrot', 'celery', 'pepper', 'tomato', 'cucumber', 'zucchini',
  'squash', 'pumpkin', 'eggplant', 'mushroom', 'broccoli', 'cauliflower',
  'cabbage', 'kale', 'spinach', 'lettuce', 'arugula', 'chard',
  'bok choy', 'pea', 'corn', 'asparagus', 'green bean', 'sprout',
  'radish', 'beet', 'turnip', 'parsnip', 'potato', 'sweet potato',
  'yam', 'cilantro', 'parsley', 'basil', 'mint', 'dill',
];
const FRUIT_KEYWORDS = [
  'apple', 'pear', 'banana', 'orange', 'lemon', 'lime', 'grape',
  'berry', 'strawberry', 'blueberry', 'raspberry', 'blackberry',
  'cranberry', 'cherry', 'peach', 'plum', 'apricot', 'mango',
  'pineapple', 'melon', 'watermelon', 'kiwi', 'fig', 'date',
  'raisin', 'avocado', 'pomegranate',
];
// Seasoning / liquid / fat patterns — checked BEFORE keyword classification.
// If an ingredient name matches any of these, we skip it entirely. The point
// of pickKeyIngredients is to surface the IDENTIFYING ingredients of a dish,
// not seasonings, sauces, oils, or stocks. We also drop the FAT category
// from the priority order entirely, for the same reason.
//
// Patterns are regex applied to the lowercased ingredient name. Word
// boundaries are used so e.g. "salt" matches "fine sea salt" but not
// "asaltedfish" (and so "stock" doesn't match "stockings"). The pepper
// rules are split: the standalone seasoning forms ("pepper", "white
// pepper", "black pepper", "ground/freshly ground white/black pepper",
// "salt and pepper") are filtered, but compound names like "thai bird
// chili peppers" or "bell pepper" pass through and reach the veggie
// classifier.
const SEASONING_PATTERNS: RegExp[] = [
  /\bsalt\b/,
  /\bsugar\b/,
  /\boil\b/,
  /\bvinegar\b/,
  /\bwine\b/,
  /\bsake\b/,
  /\bmirin\b/,
  /\bsauce\b/,
  /\bpaste\b/,
  /\bbouillon\b/,
  /\bpowder\b/,
  /\bextract\b/,
  /\bseasoning\b/,
  /\bspice\b/,
  /\bstock\b/,
  /\bbroth\b/,
  /\bwater\b/,
  /\bice\b/,
  /\bpeppercorn/,
  // Standalone-pepper-as-seasoning forms only:
  /^pepper$/,
  /^peppers$/,
  /^(white|black|ground white|ground black|freshly ground|freshly ground white|freshly ground black)\s+pepper(s)?$/,
  /^salt and pepper$/,
  /^salt and white pepper$/,
];

function isSeasoning(name: string): boolean {
  const n = name.toLowerCase().trim();
  return SEASONING_PATTERNS.some(re => re.test(n));
}

type Category = 'protein' | 'grain' | 'veggie' | 'fruit';
const ORDER: Category[] = ['protein', 'grain', 'veggie', 'fruit'];

function classify(name: string): Category | null {
  const n = name.toLowerCase();
  if (PROTEIN_KEYWORDS.some(k => n.includes(k))) return 'protein';
  if (GRAIN_KEYWORDS.some(k => n.includes(k))) return 'grain';
  if (VEGGIE_KEYWORDS.some(k => n.includes(k))) return 'veggie';
  if (FRUIT_KEYWORDS.some(k => n.includes(k))) return 'fruit';
  return null;
}

/**
 * Pick up to 4 key ingredients to display on the hover overlay.
 * Priority: one from each of protein -> grain -> veggie -> fruit
 * (in source order within each category). Seasonings, sauces, oils,
 * stocks, and pure-seasoning pepper forms are filtered out before
 * classification. If nothing classifies, fall back to the first 4
 * ingredients in source order.
 */
export function pickKeyIngredients(ingredients: Ingredient[]): string[] {
  if (!ingredients || ingredients.length === 0) return [];
  const buckets: Record<Category, string[]> = {
    protein: [], grain: [], veggie: [], fruit: [],
  };
  let anyClassified = false;
  for (const ing of ingredients) {
    if (isSeasoning(ing.name)) continue;
    const c = classify(ing.name);
    if (c) {
      buckets[c].push(ing.name);
      anyClassified = true;
    }
  }
  if (!anyClassified) {
    return ingredients.slice(0, 4).map(i => i.name.toLowerCase());
  }
  const picked: string[] = [];
  for (const c of ORDER) {
    const first = buckets[c][0];
    if (first) picked.push(first);
  }
  return picked.slice(0, 4).map(n => n.toLowerCase());
}

/**
 * Format the time line: "15 min prep · 1h 30m cook · serves 4".
 * Skips parts that are missing.
 */
export function formatTimeLine(
  prepMinutes: number | null | undefined,
  cookMinutes: number | null | undefined,
  servings: number | null | undefined,
): string {
  const parts: string[] = [];
  if (prepMinutes != null && prepMinutes > 0) {
    parts.push(`${formatMinutes(prepMinutes)} prep`);
  }
  if (cookMinutes != null && cookMinutes > 0) {
    parts.push(`${formatMinutes(cookMinutes)} cook`);
  }
  if (servings != null && servings > 0) {
    parts.push(`serves ${servings}`);
  }
  return parts.join(' · ');
}
